import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Button from '../ui/custom/Button';
import Icon from '../AppIcon';
import useDialogDismiss from '../../hooks/common/useDialogDismiss';
import { useAuth } from '../../contexts';
import {
  DISCONNECTED,
  disconnectDoordash,
  getDoordashConnection,
  startDoordashConnect,
} from '../../services/doordashConnectionService';

// A coach's own DoorDash account (DOORDASH_CONNECT_PLAN.md, step 3).
//
// DoordashConnectProvider sits around every route. A coach in the pilot who
// has not connected DoorDash cannot open Schedule Meal: every Schedule Meal
// button goes through requireDoordashConnect, which shows the Connect popup
// instead, and closing the popup leaves Schedule Meal closed. The dashboard
// card and the Profile row stay until they connect. Nothing renders unless
// the server says this account is in the pilot (`enabled`).

// Set when the coach logs out of DoorDash here. DoorDash has no logout we can
// call and its sign-in page remembers whoever is signed in to DoorDash in this
// browser, so until they connect again every sign-in asks DoorDash for its
// login screen (prompt=login) instead of quietly reusing that account.
const LOGGED_OUT_KEY = 'doordashLoggedOut';

const readLoggedOut = () => {
  try {
    return window.sessionStorage.getItem(LOGGED_OUT_KEY) === '1';
  } catch {
    return false;
  }
};

const writeLoggedOut = (value) => {
  try {
    if (value) window.sessionStorage.setItem(LOGGED_OUT_KEY, '1');
    else window.sessionStorage.removeItem(LOGGED_OUT_KEY);
  } catch {
    // Private mode: the next sign-in may reuse DoorDash's browser session.
  }
};

const connectCopy = (reconnect) => ({
  title: reconnect ? 'Reconnect your DoorDash account' : 'Connect your DoorDash account',
  body: reconnect
    ? 'Your DoorDash sign-in expired. Reconnect to keep ordering from DoorDash restaurants.'
    : 'DoorDash orders are placed and paid on your own DoorDash account, using your saved addresses and card.',
  action: reconnect ? 'Reconnect DoorDash' : 'Connect DoorDash',
});

const gateCopy = (reconnect) => ({
  ...connectCopy(reconnect),
  title: reconnect ? 'Reconnect DoorDash to schedule meals' : 'Connect DoorDash to schedule meals',
  body: reconnect
    ? 'Your DoorDash sign-in expired. Reconnect your DoorDash account before scheduling a meal.'
    : 'Connect your DoorDash account before scheduling a meal. DoorDash orders are placed and paid on your own DoorDash account, using your saved addresses and card.',
});

const DoordashConnectContext = createContext(null);

const NO_PROVIDER = {
  connection: DISCONNECTED,
  loading: false,
  hasOwnTeam: false,
  popupOpen: false,
  setConnection: () => {},
  startConnect: async () => {},
  requireDoordashConnect: (onContinue) => onContinue?.(),
};

export function useDoordashConnect() {
  return useContext(DoordashConnectContext) || NO_PROVIDER;
}

function DoordashConnectModal({ open, reconnect, starting, error, onConnect, onClose }) {
  const connectRef = useRef(null);
  useDialogDismiss(open, starting ? null : onClose, { initialFocusRef: connectRef });
  if (!open) return null;
  const copy = gateCopy(reconnect);

  return (
    <div className="fixed inset-0 z-[2050] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/60" aria-hidden onClick={starting ? undefined : onClose} />
      <div
        className="relative w-full max-w-md rounded-xl border border-border bg-card shadow-athletic-lg"
        role="dialog"
        aria-modal="true"
        aria-labelledby="doordash-connect-title"
      >
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Icon name="Link" size={20} />
          </span>
          <h2 id="doordash-connect-title" className="flex-1 text-base font-semibold text-foreground">
            {copy.title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={starting}
            aria-label="Close"
            className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        <div className="px-4 py-4 space-y-3">
          <p className="text-sm text-muted-foreground leading-relaxed">{copy.body}</p>
          <p className="text-sm text-muted-foreground leading-relaxed">
            You&apos;ll sign in on DoorDash and come straight back here.
          </p>
          {error && (
            <p className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded px-3 py-2">
              {error}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2 border-t border-border bg-muted/40 px-4 py-3 md:flex-row md:justify-end">
          <Button type="button" variant="ghost" onClick={onClose} disabled={starting}>
            Not now
          </Button>
          <Button
            ref={connectRef}
            type="button"
            onClick={onConnect}
            disabled={starting}
            iconName="ExternalLink"
            iconPosition="left"
          >
            {starting ? 'Opening DoorDash…' : copy.action}
          </Button>
        </div>
      </div>
    </div>
  );
}

// The ?doordash=connected|error result the callback appends to whatever page
// the coach started from. Shown once, on any page, then dropped from the URL.
function ResultToast({ result, onClose }) {
  useEffect(() => {
    if (!result) return undefined;
    const timer = window.setTimeout(onClose, 6000);
    return () => window.clearTimeout(timer);
  }, [result, onClose]);
  if (!result) return null;
  return (
    <div className="fixed inset-x-0 top-20 z-[2040] flex justify-center px-4 pointer-events-none">
      <div
        role="status"
        className={`pointer-events-auto flex max-w-md items-start gap-3 rounded-lg border px-4 py-3 text-sm shadow-athletic-lg ${
          result.ok
            ? 'border-success/30 bg-card text-success'
            : 'border-destructive/30 bg-card text-destructive'
        }`}
      >
        <Icon name={result.ok ? 'CheckCircle' : 'AlertCircle'} size={18} className="mt-0.5 shrink-0" />
        <span className="flex-1">{result.message}</span>
        <button type="button" onClick={onClose} aria-label="Dismiss" className="text-muted-foreground hover:text-foreground">
          <Icon name="X" size={16} />
        </button>
      </div>
    </div>
  );
}

export function DoordashConnectProvider({ children }) {
  const { user, teams } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const userId = user?.id || null;
  const hasOwnTeam = Array.isArray(teams) && teams.length > 0;

  const [connection, setConnection] = useState(DISCONNECTED);
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);

  // The connection belongs to the signed-in user; re-read it when they change.
  useEffect(() => {
    let cancelled = false;
    if (!userId) {
      setConnection(DISCONNECTED);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    getDoordashConnection()
      .then((next) => { if (!cancelled) setConnection(next); })
      .catch(() => { if (!cancelled) setConnection(DISCONNECTED); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userId]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get('doordash');
    if (!status) return;
    if (status === 'connected') writeLoggedOut(false);
    setResult({
      ok: status === 'connected',
      message: status === 'connected'
        ? 'DoorDash connected.'
        : (params.get('doordashMessage') || 'DoorDash was not connected.'),
    });
    params.delete('doordash');
    params.delete('doordashMessage');
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true, state: location.state });
  }, [location.pathname, location.search, location.state, navigate]);

  const needsConnect = connection.enabled && !connection.connected;

  const startConnect = useCallback(async (returnTo) => {
    setStarting(true);
    setError(null);
    try {
      await startDoordashConnect(returnTo || `${window.location.pathname}${window.location.search}`, {
        forceLogin: readLoggedOut(),
      });
    } catch (startError) {
      setError(startError.message);
      setStarting(false);
      throw startError;
    }
  }, []);

  // Closing never runs what was asked for: Schedule Meal stays shut.
  const closePopup = useCallback(() => {
    setPopupOpen(false);
    setError(null);
  }, []);

  // A Schedule Meal click before the connection has loaded waits for it
  // rather than slipping through.
  const queuedRef = useRef(null);

  /**
   * Run `onContinue` (open Schedule Meal) only when this user may: anyone
   * outside the pilot, or a pilot coach whose DoorDash is connected. Everyone
   * else gets the Connect popup, every time, and `onContinue` never runs.
   */
  const requireDoordashConnect = useCallback((onContinue) => {
    if (loading) {
      queuedRef.current = onContinue;
      return;
    }
    if (needsConnect) {
      setPopupOpen(true);
      return;
    }
    onContinue?.();
  }, [loading, needsConnect]);

  useEffect(() => {
    if (loading || !queuedRef.current) return;
    const queued = queuedRef.current;
    queuedRef.current = null;
    requireDoordashConnect(queued);
  }, [loading, requireDoordashConnect]);

  const clearResult = useCallback(() => setResult(null), []);

  const value = useMemo(() => ({
    connection,
    loading,
    hasOwnTeam,
    popupOpen,
    setConnection,
    startConnect,
    requireDoordashConnect,
  }), [connection, loading, hasOwnTeam, popupOpen, startConnect, requireDoordashConnect]);

  return (
    <DoordashConnectContext.Provider value={value}>
      {children}
      <DoordashConnectModal
        open={popupOpen && needsConnect}
        reconnect={connection.status === 'needs_reconnect'}
        starting={starting}
        error={error}
        onConnect={() => startConnect().catch(() => {})}
        onClose={closePopup}
      />
      <ResultToast result={result} onClose={clearResult} />
    </DoordashConnectContext.Provider>
  );
}

/** Dashboard card: stays until the coach connects. */
/**
 * "Connect DoorDash" where a page couldn't read DoorDash because this pilot
 * coach isn't signed in to it (doordash_reconnect_required): opens the
 * connect popup; DoorDash returns them to the same page, signed in.
 */
export function DoordashConnectButton({ className, size = 'sm' }) {
  const { connection, requireDoordashConnect } = useDoordashConnect();
  return (
    <Button
      type="button"
      size={size}
      className={className}
      iconName="ExternalLink"
      iconPosition="left"
      onClick={() => requireDoordashConnect(() => window.location.reload())}
      data-testid="doordash-connect-button"
    >
      {connection?.status === 'needs_reconnect' ? 'Reconnect DoorDash' : 'Connect DoorDash'}
    </Button>
  );
}

export function DoordashConnectCard() {
  const { connection, loading, hasOwnTeam, popupOpen, startConnect } = useDoordashConnect();
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);

  if (loading || !connection.enabled || connection.connected || !hasOwnTeam || popupOpen) return null;
  const copy = connectCopy(connection.status === 'needs_reconnect');

  const handleConnect = async () => {
    setStarting(true);
    setError(null);
    try {
      await startConnect('/dashboard-home');
    } catch (startError) {
      setError(startError.message);
      setStarting(false);
    }
  };

  return (
    <section className="mb-8 bg-card border border-border rounded-xl shadow-athletic p-6 space-y-4">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 shrink-0 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
            <Icon name="Link" size={18} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">{copy.title}</h2>
            <p className="text-sm text-muted-foreground">{copy.body}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button onClick={handleConnect} disabled={starting} iconName="ExternalLink" iconPosition="left">
            {starting ? 'Opening DoorDash…' : copy.action}
          </Button>
        </div>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </section>
  );
}

/**
 * Profile page row: status plus Sign in / Reconnect / Log out of DoorDash.
 * Logging out forgets the coach's DoorDash sign-in here; DoorDash itself
 * offers no logout we can call, so the next sign-in asks DoorDash for its
 * login screen (see LOGGED_OUT_KEY). That is also how a coach switches to a
 * different DoorDash account: log out, then sign in.
 */
export function DoordashAccountSection({ confirm = null }) {
  const { connection, setConnection, loading, startConnect } = useDoordashConnect();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [justLoggedOut, setJustLoggedOut] = useState(false);

  if (loading || !connection.enabled) return null;

  const handleConnect = async () => {
    setBusy(true);
    setError(null);
    try {
      await startConnect('/profile');
    } catch (startError) {
      setError(startError.message);
      setBusy(false);
    }
  };

  const handleLogout = async () => {
    if (confirm) {
      const ok = await confirm({
        title: 'Log out of DoorDash?',
        message: 'MealOps will forget your DoorDash sign-in. You won’t be able to schedule meals until you sign in to a DoorDash account again.',
        confirmLabel: 'Log out',
        variant: 'destructive',
      });
      if (!ok) return;
    }
    setBusy(true);
    setError(null);
    try {
      await disconnectDoordash();
      writeLoggedOut(true);
      setConnection({ ...connection, connected: false, status: null });
      setJustLoggedOut(true);
    } catch (logoutError) {
      setError(logoutError.message);
    } finally {
      setBusy(false);
    }
  };

  const statusLabel = connection.connected
    ? 'Signed in'
    : connection.status === 'needs_reconnect'
      ? 'Sign-in expired'
      : 'Not signed in';

  return (
    <section className="bg-card border border-border rounded-xl shadow-athletic p-6 space-y-4">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 shrink-0 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
            <Icon name="Link" size={18} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">DoorDash account</h2>
            <p className="text-sm text-muted-foreground">
              {statusLabel}. DoorDash orders use your own DoorDash account, addresses and card.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          {connection.connected ? (
            <Button variant="outline" onClick={handleLogout} disabled={busy} iconName="LogOut" iconPosition="left">
              {busy ? 'Logging out…' : 'Log out of DoorDash'}
            </Button>
          ) : (
            <Button onClick={handleConnect} disabled={busy} iconName="ExternalLink" iconPosition="left">
              {busy
                ? 'Opening DoorDash…'
                : connection.status === 'needs_reconnect' ? 'Reconnect DoorDash' : 'Sign in to DoorDash'}
            </Button>
          )}
        </div>
      </div>
      {justLoggedOut && !connection.connected && (
        <div role="status" className="rounded-lg border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
          Logged out of DoorDash. Next time you sign in, DoorDash will ask which account to use. If it signs you
          straight back in to the same account, sign out at doordash.com in this browser first.
        </div>
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}
    </section>
  );
}
